// src/pages/university-admin/UserManagement.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    InputAdornment,
    Badge,
    useMediaQuery,
    Stack,
    Snackbar
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search,
    Person,
    Email,
    School,
    Work,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Block,
    AdminPanelSettings,
    VerifiedUser,
    Pending,
    Visibility,
    Edit,
    Delete,
    FilterList,
    People,
    TrendingUp,
    Assignment,
    History,
    ThumbUp,
    ThumbDown,
    Gavel,
    AccountCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIUserManagementList,
    APIUserManagementStats,
    APIUserManagementUpdateStatus,
    APIUserManagementApprove,
    APIUserManagementReject,
    APIUserManagementLogs,
    APIStudentProfileImage,
    APISupervisorProfileImage,
    APIUserManagementByUniversity,
    APIUserManagementUniversityStats
} from '../../apiServices/APIs';
import {
    formatCreatedAtDate,
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'U';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const UserManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [statusForm, setStatusForm] = useState({ status: '', reason: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: '', title: '', message: '' });
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");


    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchUsers(),
                fetchStats(),
                fetchLogs()
            ]);
        } catch (err) {
            setError("Failed to load user data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchUsers = async () => {
        const universityId = getUserDetails("universityId");
        const result = await apiService.listById(APIUserManagementByUniversity,universityId);
        if (result.success && isMounted.current) {
            setUsers(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchStats = async () => {
        const universityId = getUserDetails("universityId");
        const result = await apiService.listById(APIUserManagementUniversityStats,universityId);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const fetchLogs = async () => {
        const result = await apiService.list(APIUserManagementLogs);
        if (result.success && isMounted.current) {
            setLogs(Array.isArray(result.data) ? result.data : []);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedUser) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('user_id', selectedUser.user_id);
            formData.append('user_type', selectedUser.user_type);
            formData.append('account_status', statusForm.status);
            if (statusForm.reason) formData.append('reason', statusForm.reason);
            
            const result = await apiService.update(APIUserManagementUpdateStatus, formData);
            
            if (result && result.success) {
                showToast("Success", `Account status updated to ${statusForm.status}`, "success");
                fetchAllData();
                handleCloseStatusDialog();
            } else {
                showToast("Error", result?.message || "Failed to update status", "error");
            }
        } catch (error) {
            console.error("Update status error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to update status";
            showToast("Error", errorMessage, "error");
        } finally {
            setActionLoading(false);
        }
    };


    const handleApproveUser = async (user) => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('user_id', user.user_id);
            formData.append('user_type', user.user_type);
            
            const result = await apiService.update(APIUserManagementApprove, formData);
            
            if (result && result.success) {
                showToast("Success", `${user.full_name} has been approved successfully`, "success");
                fetchAllData();
                setConfirmDialog({ ...confirmDialog, open: false });
            } else {
                showToast("Error", result?.message || "Failed to approve user", "error");
            }
        } catch (error) {
            console.error("Approve user error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to approve user";
            showToast("Error", errorMessage, "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectUser = async (user) => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('user_id', user.user_id);
            formData.append('user_type', user.user_type);
            formData.append('reason', 'Registration rejected by administrator');
            
            const result = await apiService.update(APIUserManagementReject, formData);
            
            if (result && result.success) {
                showToast("Success", `${user.full_name} has been rejected`, "success");
                fetchAllData();
                setConfirmDialog({ ...confirmDialog, open: false });
            } else {
                showToast("Error", result?.message || "Failed to reject user", "error");
            }
        } catch (error) {
            console.error("Reject user error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to reject user";
            showToast("Error", errorMessage, "error");
        } finally {
            setActionLoading(false);
        }
    };
    const handleOpenStatusDialog = (user) => {
        setSelectedUser(user);
        setStatusForm({ status: user.account_status, reason: '' });
        setOpenStatusDialog(true);
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedUser(null);
        setStatusForm({ status: '', reason: '' });
    };

    const openConfirmDialog = (user, action) => {
        const config = {
            approve: {
                title: 'Approve User',
                message: `Are you sure you want to approve ${user.full_name}? This will activate their account.`,
                action: () => handleApproveUser(user)
            },
            reject: {
                title: 'Reject User',
                message: `Are you sure you want to reject ${user.full_name}? This will deactivate their account.`,
                action: () => handleRejectUser(user)
            }
        };
        
        setConfirmDialog({
            open: true,
            user,
            action,
            title: config[action].title,
            message: config[action].message,
            onConfirm: config[action].action
        });
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'active': return <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />;
            case 'suspended': return <Block sx={{ color: '#f44336', fontSize: 16 }} />;
            case 'blocked': return <Warning sx={{ color: '#f44336', fontSize: 16 }} />;
            case 'deactivated': return <Cancel sx={{ color: '#9e9e9e', fontSize: 16 }} />;
            default: return <Schedule sx={{ color: '#ff9800', fontSize: 16 }} />;
        }
    };

    const getProfileImageUrl = (user) => {
        if (user?.profile_image_path) {
            // Get the appropriate image API based on user type
            const imageApi = user.user_type === 'student'
                ? APIStudentProfileImage
                : APISupervisorProfileImage;
            return getImageUrl(imageApi, user.profile_image_path);
        }
        return null;
    };

    const filteredUsers = users.filter(user => 
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return <LoadingState message="Loading Users..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Users" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <InstitutionAdminBar notificationCount={89} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <AdminPanelSettings /> User Management
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>
                </Box>
                
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mt: 2
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab icon={<People />} iconPosition="start" label="All Users" />
                        <Tab icon={<History />} iconPosition="start" label="Activity Logs" />
                    </Tabs>

                    {/* Users Tab */}
                    {activeTab === 0 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {/* Search Bar */}
                            <Paper sx={{ 
                                mb: 3, 
                                p: { xs: 1.5, sm: 2 }, 
                                display: 'flex', 
                                alignItems: 'center',
                                backgroundColor: theme.palette.grey[50],
                                borderRadius: 2
                            }}>
                                <Search sx={{ mx: 1, color: 'text.secondary' }} />
                                <TextField 
                                    fullWidth 
                                    placeholder="Search by name, email, or role..." 
                                    variant="standard" 
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)} 
                                    InputProps={{ disableUnderline: true }}
                                    size={isMobile ? "small" : "medium"}
                                />
                            </Paper>

                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1000 } }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                            <TableCell><strong>User</strong></TableCell>
                                            <TableCell><strong>Email</strong></TableCell>
                                            <TableCell><strong>Role</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Verification</strong></TableCell>
                                            <TableCell><strong>Registered</strong></TableCell>
                                            <TableCell align="center"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, idx) => {
                                            const avatarColor = stringToColor(user.full_name);
                                            const initials = getInitials(user.full_name);
                                            const profileImage = getProfileImageUrl(user);
                                            return (
                                                <TableRow key={user.user_id} hover>
                                                    <TableCell>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 2,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            textAlign: { xs: 'center', sm: 'left' }
                                                        }}>
                                                            <Avatar 
                                                                src={profileImage}
                                                                sx={{ 
                                                                    width: { xs: 40, sm: 48 }, 
                                                                    height: { xs: 40, sm: 48 },
                                                                    bgcolor: !profileImage ? avatarColor : 'transparent',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {!profileImage && initials}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ 
                                                                    fontWeight: 600,
                                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                }}>
                                                                    {user.full_name}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {user.email}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            icon={user.user_type === 'student' ? <School /> : <Work />} 
                                                            label={user.user_type?.toUpperCase()} 
                                                            size="small" 
                                                            color={user.user_type === 'student' ? 'primary' : 'secondary'} 
                                                            variant="outlined"
                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            icon={getStatusIcon(user.account_status)} 
                                                            label={user.formatted_account_status} 
                                                            size="small" 
                                                            color={user.account_status_color}
                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            icon={user.verification_status === 'verified' ? <VerifiedUser /> : <Pending />} 
                                                            label={user.formatted_verification_status} 
                                                            size="small" 
                                                            color={user.verification_status === 'verified' ? 'success' : 'warning'}
                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {formatCreatedAtDate(user.registration_date)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                            {user.approval_status === 'pending' && (
                                                                <>
                                                                    <Tooltip title="Approve User">
                                                                        <IconButton 
                                                                            size="small" 
                                                                            sx={{ color: theme.palette.success.main }}
                                                                            onClick={() => openConfirmDialog(user, 'approve')}
                                                                        >
                                                                            <ThumbUp fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Reject User">
                                                                        <IconButton 
                                                                            size="small" 
                                                                            sx={{ color: theme.palette.error.main }}
                                                                            onClick={() => openConfirmDialog(user, 'reject')}
                                                                        >
                                                                            <ThumbDown fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                            <Tooltip title="Change Status">
                                                                <IconButton size="small" onClick={() => handleOpenStatusDialog(user)}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={filteredUsers.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                onRowsPerPageChange={(e) => { 
                                    setRowsPerPage(parseInt(e.target.value, 10)); 
                                    setPage(0); 
                                }}
                                sx={{
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Logs Tab */}
                    {activeTab === 1 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: { xs: 600, sm: 700, md: 800 } }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                            <TableCell><strong>Time</strong></TableCell>
                                            <TableCell><strong>Admin</strong></TableCell>
                                            <TableCell><strong>Action</strong></TableCell>
                                            <TableCell><strong>Description</strong></TableCell>
                                            <TableCell><strong>Target</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {logs.map((log) => (
                                            <TableRow key={log.id} hover>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {log.time_ago}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {log.admin_name}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={log.formatted_action} 
                                                        size="small" 
                                                        color={log.action_color}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {log.action_description}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {log.target_user_type}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {logs.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                        No logs available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Paper>

                {/* Update Status Dialog */}
                <Dialog 
                    open={openStatusDialog} 
                    onClose={handleCloseStatusDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Update Account Status
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    value={statusForm.status} 
                                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} 
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="deactivated">Deactivated</MenuItem>
                                    <MenuItem value="blocked">Blocked</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Reason (Optional)" 
                                multiline 
                                rows={3} 
                                value={statusForm.reason} 
                                onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })} 
                                placeholder="Provide reason for status change..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseStatusDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateStatus} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Action Dialog */}
                <Dialog
                    open={confirmDialog.open}
                    onClose={handleCloseConfirmDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 1
                        }}>
                            {confirmDialog.action === 'approve' ? (
                                <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                            ) : (
                                <Cancel sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                            )}
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {confirmDialog.title}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {confirmDialog.message}
                        </Typography>
                        {confirmDialog.user && (
                            <Box sx={{ 
                                p: 2, 
                                bgcolor: theme.palette.grey[50], 
                                borderRadius: 2,
                                mt: 2
                            }}>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                    <Avatar 
                                        src={getProfileImageUrl(confirmDialog.user)}
                                        sx={{ 
                                            width: 48, 
                                            height: 48,
                                            bgcolor: !getProfileImageUrl(confirmDialog.user) ? stringToColor(confirmDialog.user.full_name) : 'transparent'
                                        }}
                                    >
                                        {!getProfileImageUrl(confirmDialog.user) && getInitials(confirmDialog.user.full_name)}
                                    </Avatar>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {confirmDialog.user.full_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {confirmDialog.user.email}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
                        <Button 
                            onClick={handleCloseConfirmDialog} 
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                            sx={{ minWidth: 100 }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={confirmDialog.onConfirm} 
                            variant="contained" 
                            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
                            disabled={actionLoading}
                            size={isMobile ? "small" : "medium"}
                            sx={{ minWidth: 100 }}
                        >
                            {actionLoading ? <CircularProgress size={20} /> : confirmDialog.action === 'approve' ? 'Approve' : 'Reject'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default UserManagement;